import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { DefaultRootState } from '@reduxLocal/persistState';

/**
 * Global hook that watches companyNumber changes and automatically invalidates
 * all company-aware queries when the company context changes.
 * 
 * This should be used at the App level to ensure all queries are properly
 * invalidated when switching companies, without needing to add useEffect hooks
 * in every component.
 * 
 * @example
 * // In App.tsx or App.native.tsx
 * function App() {
 *   useCompanyQueryInvalidation();
 *   // ... rest of app
 * }
 */
export const useCompanyQueryInvalidation = () => {
  const queryClient = useQueryClient();
  const company = useSelector((state: DefaultRootState) => state.promiseData.data?.companyData || null);
  const companyNumber = company?._id || company?.id || null;

  // Track previous companyNumber to detect changes
  const prevCompanyNumberRef = useRef<string | null>(companyNumber);

  useEffect(() => {
    const prevCompanyNumber = prevCompanyNumberRef.current;
    const currentCompanyNumber = companyNumber;

    // Only invalidate if companyNumber actually changed
    if (prevCompanyNumber !== currentCompanyNumber) {
      console.log('🔄 [useCompanyQueryInvalidation] Company changed, invalidating queries:', {
        prevCompanyNumber,
        currentCompanyNumber,
        timestamp: new Date().toISOString()
      });

      // Invalidate all billing-related queries
      // These queries should refetch with the new company context
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'subscriptionPlan'
      });

      queryClient.invalidateQueries({ queryKey: ['billingInfo'] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billingInfo'
      });

      queryClient.invalidateQueries({ queryKey: ['planData'] });
      queryClient.invalidateQueries({ queryKey: ['planSubscriptions'] });

      // Invalidate other company-aware queries
      // Add more query keys here as needed for company-specific data
      queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['companyMembers'] });
      queryClient.invalidateQueries({ queryKey: ['statCount'] });

      // Invalidate data-related queries that are company-aware
      // These queries rely on session companyNumber, so they must be invalidated
      queryClient.invalidateQueries({ queryKey: ['data-folders'] });
      queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
      queryClient.invalidateQueries({ queryKey: ['dataFoldersPaginated'] });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          // Invalidate queries that are typically company-aware
          return [
            'data-folders', // React Query key format
            'dataFolders',
            'dataFoldersPaginated',
            'data',
            'dataContent',
            'data-content', // React Query key format
            'userStats',
            'userStatsTrends',
            'integrationData'
          ].includes(key as string);
        }
      });

      // Update ref for next comparison
      prevCompanyNumberRef.current = currentCompanyNumber;
    }
  }, [companyNumber, queryClient]);
};

export default useCompanyQueryInvalidation;

